import { useState } from 'react'
import { Alert, Modal, Select, Typography } from 'antd'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { fetchRouters, routerLabel } from './networkApi'

export interface RoutersTarget {
  title: string
  hint: string
  nasIds: readonly number[]
  /** Tanlash mumkin bo'lgan routerlar; `null` — tashkilotning hammasi */
  allowedNasIds: readonly number[] | null
  save: (nasIds: number[]) => Promise<unknown>
}

export function RoutersModal({ target, onClose }: { target: RoutersTarget | null; onClose: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<number[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const routers = useQuery({
    queryKey: queryKeys.network.routers,
    queryFn: fetchRouters,
    enabled: target !== null,
  })

  const all = routers.data ?? []
  const known = selected.filter((id) => all.some((r) => r.nasId === id))
  const options = all
    .filter((r) => target?.allowedNasIds == null || target.allowedNasIds.includes(r.nasId) || known.includes(r.nasId))
    .map((r) => ({ value: r.nasId, label: routerLabel(r) }))

  const submit = async () => {
    if (!target) return
    setSaving(true)
    setError(null)
    try {
      await target.save(known)
      void queryClient.invalidateQueries({ queryKey: queryKeys.network.all })
      onClose()
    } catch (err) {
      setError(translateError(err, t))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={target !== null}
      title={target?.title}
      onCancel={onClose}
      onOk={() => void submit()}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={saving}
      okButtonProps={{ disabled: !routers.isSuccess }}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (!visible) return
        setError(null)
        setSelected([...(target?.nasIds ?? [])])
      }}
    >
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} /> : null}
      {routers.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(routers.error, t)} />
      ) : null}
      <Typography.Paragraph type="secondary">{target?.hint}</Typography.Paragraph>
      <Select<number[]>
        mode="multiple"
        style={{ width: '100%' }}
        value={known}
        onChange={setSelected}
        loading={routers.isPending}
        disabled={saving || !routers.isSuccess}
        placeholder={t('network.visps.routersPlaceholder')}
        optionFilterProp="label"
        options={options}
      />
    </Modal>
  )
}
